/**
 * Sistema di throttling per errori Sentry
 * Riduce il numero di errori identici inviati a Sentry per evitare rate limiting
 */

class ErrorThrottle {
  constructor() {
    this.errorCounts = new Map();
    this.resetInterval = 60000; // Reset ogni minuto
    this.maxErrorsPerType = 5; // Massimo 5 errori dello stesso tipo per minuto
    
    // Reset automatico dei conteggi ogni minuto
    setInterval(() => {
      this.errorCounts.clear();
    }, this.resetInterval);
  }

  /**
   * Verifica se un errore dovrebbe essere inviato a Sentry
   * @param {Error} error - L'errore da verificare
   * @returns {boolean} - True se l'errore deve essere inviato
   */
  shouldSendError(error) {
    if (!error || !error.message) return false;

    // Crea una chiave unica per il tipo di errore
    const errorKey = this.createErrorKey(error);
    
    // Ottieni il conteggio corrente per questo tipo di errore
    const currentCount = this.errorCounts.get(errorKey) || 0;
    
    // Se abbiamo già raggiunto il limite, non inviare
    if (currentCount >= this.maxErrorsPerType) {
      console.warn(`Error throttled: ${errorKey} (count: ${currentCount})`);
      return false;
    }
    
    // Incrementa il conteggio e permetti l'invio
    this.errorCounts.set(errorKey, currentCount + 1);
    return true;
  }

  /**
   * Crea una chiave unica per il tipo di errore
   * @param {Error} error - L'errore
   * @returns {string} - Chiave univoca
   */
  createErrorKey(error) {
    // Normalizza il messaggio di errore per raggruppare errori simili
    const normalizedMessage = error.message
      .replace(/\d+/g, 'N') // Sostituisce numeri con 'N'
      .replace(/https?:\/\/[^\s]+/g, 'URL') // Sostituisce URL con 'URL'
      .replace(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, 'UUID'); // Sostituisce UUID
    
    return `${error.name || 'Error'}:${normalizedMessage}:${error.stack?.split('\n')[1]?.trim() || 'unknown'}`;
  }

  /**
   * Filtra errori noti che non dovrebbero essere inviati a Sentry
   * @param {Error} error - L'errore da filtrare
   * @returns {boolean} - True se l'errore deve essere filtrato (non inviato)
   */
  shouldFilterError(error) {
    if (!error || !error.message) return true;

    const message = error.message.toLowerCase();
    const stack = (error.stack || '').toLowerCase();
    
    // Lista di errori da ignorare
    const ignoredErrors = [
      'network error',
      'failed to fetch',
      'load failed',
      'script error',
      'non-error promise rejection',
      'cancelled',
      'aborted',
      'connection closed',
      'timeout',
      'iubenda',
      'core-it.js',
      'autoblocking', // Iubenda autoblocking script
      "reading 'cs'", // Iubenda CS error
      "reading 'cmp'", // Iubenda CMP error
      'extension context invalidated', // Errori da estensioni browser
      'reading \'ontouchstart\'', // Errori di compatibilità touch
      'canvas.contentdocument', // Errori di iframe
      'app-offline', // Firebase Installations offline (comune in sviluppo)
      'installations/app-offline', // Firebase error code
      'application offline', // Variante del messaggio Firebase
      'resizeobserver loop', // ResizeObserver (warning noto, non critico)
      'loading chunk', // Webpack code-splitting chunk load failure (rete instabile)
      'loading css chunk', // CSS chunk load failure
      'dynamically imported module', // Dynamic import failure (rete instabile)
      'googletag', // Google Tag Manager errors
      'stape', // Stape.net analytics errors
      'gtm.js', // GTM script errors
      'non-error exception captured', // Sentry wrapping non-Error objects
      'permission_denied', // Firebase permission denied durante offline/reload
      'quota-exceeded', // Firestore quota errors (transitori)
      'unavailable', // Firestore offline/unavailable (transitorio)
      'database deleted by request of the user', // Firestore IndexedDB deletion (clear browser data / Safari multi-tab)
    ];
    
    // Controlla il messaggio
    if (ignoredErrors.some(ignored => message.includes(ignored))) {
      return true;
    }
    
    // Controlla anche lo stack trace per errori da terze parti
    const ignoredStacks = [
      'iubenda', 'autoblocking', 'core-it',
      'chrome-extension://', // Errori da estensioni Chrome
      'moz-extension://', // Errori da estensioni Firefox
      'safari-extension://', // Errori da estensioni Safari
      'googletag',
      'stape.net'
    ];
    if (ignoredStacks.some(ignored => stack.includes(ignored))) {
      return true;
    }
    
    return false;
  }
}

// Crea un'istanza globale del throttle
export const errorThrottle = new ErrorThrottle();

/**
 * Wrapper per Sentry che applica il throttling
 * @param {Error} error - L'errore da inviare
 * @param {Object} extra - Dati extra da inviare con l'errore
 * @returns {boolean} - True se l'errore è stato inviato
 */
export const sendErrorToSentry = (error, extra = {}) => {
  // Verifica se l'errore deve essere filtrato
  if (errorThrottle.shouldFilterError(error)) {
    console.debug('Error filtered from Sentry:', error.message);
    return false;
  }

  // Verifica il throttling
  if (!errorThrottle.shouldSendError(error)) {
    return false;
  }

  // Invia a Sentry se è disponibile
  if (window.Sentry && window.Sentry.captureException) {
    try {
      const sentryExtra = { ...extra };
      const sentryTags = {};

      // Arricchisci il contesto per AppError (hanno code, userMessage, context)
      if (error.code && error.context && error.userMessage) {
        sentryExtra.errorCode = error.code;
        sentryExtra.errorContext = error.context;
        sentryExtra.userMessage = error.userMessage;
        sentryTags.error_code = error.code;
      }

      window.Sentry.captureException(error, {
        extra: sentryExtra,
        tags: sentryTags
      });
      console.debug('Error sent to Sentry:', error.message);
      return true;
    } catch (sentryError) {
      console.warn('Failed to send error to Sentry:', sentryError);
      return false;
    }
  }

  return false;
};
